/**
 * International country + subdivision model, sibling to the US region table.
 *
 * Supports expanding the airport directory beyond the US (FAA/NASR stays the
 * authority for US airports) to Canada, Mexico, and the Caribbean. Kept in a
 * separate module from the US region map so the existing US exports/behavior are
 * untouched — consumers import what they need from the package barrel.
 *
 * - CA_PROVINCES / MX_STATES: ISO 3166-2 subdivision code + English name.
 * - CARIBBEAN_COUNTRIES: ISO 3166-1 alpha-2 code + name for Caribbean nations
 *   and territories that have ICAO airports. Puerto Rico (PR) and the US Virgin
 *   Islands (VI) are US territories already in-system and are intentionally
 *   EXCLUDED here.
 * - SUPPORTED_COUNTRIES: US, CA, MX, plus the Caribbean list, with display
 *   names. `countryName` / `subdivisionsForCountry` are the shared helpers.
 */

export interface Subdivision {
  /** ISO 3166-2 subdivision code without the country prefix (e.g. "ON", "QC"). */
  code: string;
  name: string;
}

export interface Country {
  /** ISO 3166-1 alpha-2 code (e.g. "CA", "MX", "BS"). */
  code: string;
  name: string;
}

/** The 13 Canadian provinces and territories (ISO 3166-2:CA). */
export const CA_PROVINCES: readonly Subdivision[] = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

/** The 32 Mexican states (31 states + Mexico City) (ISO 3166-2:MX). */
export const MX_STATES: readonly Subdivision[] = [
  { code: "AGU", name: "Aguascalientes" },
  { code: "BCN", name: "Baja California" },
  { code: "BCS", name: "Baja California Sur" },
  { code: "CAM", name: "Campeche" },
  { code: "CHP", name: "Chiapas" },
  { code: "CHH", name: "Chihuahua" },
  { code: "CMX", name: "Ciudad de México" },
  { code: "COA", name: "Coahuila" },
  { code: "COL", name: "Colima" },
  { code: "DUR", name: "Durango" },
  { code: "GUA", name: "Guanajuato" },
  { code: "GRO", name: "Guerrero" },
  { code: "HID", name: "Hidalgo" },
  { code: "JAL", name: "Jalisco" },
  { code: "MEX", name: "México" },
  { code: "MIC", name: "Michoacán" },
  { code: "MOR", name: "Morelos" },
  { code: "NAY", name: "Nayarit" },
  { code: "NLE", name: "Nuevo León" },
  { code: "OAX", name: "Oaxaca" },
  { code: "PUE", name: "Puebla" },
  { code: "QUE", name: "Querétaro" },
  { code: "ROO", name: "Quintana Roo" },
  { code: "SLP", name: "San Luis Potosí" },
  { code: "SIN", name: "Sinaloa" },
  { code: "SON", name: "Sonora" },
  { code: "TAB", name: "Tabasco" },
  { code: "TAM", name: "Tamaulipas" },
  { code: "TLA", name: "Tlaxcala" },
  { code: "VER", name: "Veracruz" },
  { code: "YUC", name: "Yucatán" },
  { code: "ZAC", name: "Zacatecas" },
];

/**
 * Caribbean nations and territories that have ICAO airports. ISO 3166-1
 * alpha-2 code + display name. Puerto Rico (PR) and the US Virgin Islands (VI)
 * are US territories already served by the FAA/NASR importer and are EXCLUDED.
 */
export const CARIBBEAN_COUNTRIES: readonly Country[] = [
  { code: "BS", name: "Bahamas" },
  { code: "CU", name: "Cuba" },
  { code: "DO", name: "Dominican Republic" },
  { code: "HT", name: "Haiti" },
  { code: "JM", name: "Jamaica" },
  { code: "KY", name: "Cayman Islands" },
  { code: "TC", name: "Turks and Caicos Islands" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AI", name: "Anguilla" },
  { code: "BB", name: "Barbados" },
  { code: "DM", name: "Dominica" },
  { code: "GD", name: "Grenada" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "MS", name: "Montserrat" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "VG", name: "British Virgin Islands" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "AW", name: "Aruba" },
  { code: "CW", name: "Curaçao" },
  { code: "SX", name: "Sint Maarten" },
  { code: "BQ", name: "Caribbean Netherlands" },
  { code: "GP", name: "Guadeloupe" },
  { code: "MQ", name: "Martinique" },
  { code: "BL", name: "Saint Barthélemy" },
  { code: "MF", name: "Saint Martin" },
  { code: "BM", name: "Bermuda" },
];

/** ISO alpha-2 codes for the Caribbean set (convenience for scope checks). */
export const CARIBBEAN_COUNTRY_CODES: readonly string[] =
  CARIBBEAN_COUNTRIES.map((c) => c.code);

/**
 * All countries the airport directory supports, with display names: the US, plus
 * the newly added Canada, Mexico, and the Caribbean list.
 */
export const SUPPORTED_COUNTRIES: readonly Country[] = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "MX", name: "Mexico" },
  ...CARIBBEAN_COUNTRIES,
];

const COUNTRY_NAME_BY_CODE: ReadonlyMap<string, string> = new Map(
  SUPPORTED_COUNTRIES.map((c) => [c.code, c.name]),
);

/**
 * Display name for a supported ISO alpha-2 country code (case-insensitive).
 * Returns the uppercased code itself when the country is not in the supported
 * set — callers never get a fabricated name.
 */
export function countryName(code: string): string {
  const upper = code.trim().toUpperCase();
  return COUNTRY_NAME_BY_CODE.get(upper) ?? upper;
}

// Reuse the US state list already encoded in the region map (union of every
// broad region) so US subdivisions stay in exactly one source of truth.
const US_STATE_CODES: readonly string[] = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS",
  "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK",
  "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV",
  "WI", "WY", "DC",
];

const US_SUBDIVISIONS: readonly Subdivision[] = US_STATE_CODES.map((code) => ({
  code,
  name: code,
}));

/**
 * Subdivisions (states/provinces) for a country code, for building filter
 * dropdowns: CA → provinces, MX → states, US → states, everything else
 * (Caribbean) → none. Case-insensitive.
 */
export function subdivisionsForCountry(code: string): readonly Subdivision[] {
  switch (code.trim().toUpperCase()) {
    case "US":
      return US_SUBDIVISIONS;
    case "CA":
      return CA_PROVINCES;
    case "MX":
      return MX_STATES;
    default:
      return [];
  }
}
